// src/pages/Booking.js
import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom"; // Import Link for gallery buttons
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Carousel from "../components/Carousel";
import {
  placeholderBookedDates,
  bookingPackages,
  faqs,
  testimonials,
} from "../data/bookingData";

const Booking = () => {
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState("");

  const currentLanguage = i18n.language.split("-")[0];

  // Memoized hooks to get the correct language data
  const langPackages = useMemo(
    () => bookingPackages[currentLanguage] || bookingPackages.en,
    [currentLanguage]
  );
  const allPackages = useMemo(
    () => [
      ...(langPackages.personal || []),
      ...(langPackages.commercial || []),
    ],
    [langPackages]
  );
  const langFaqs = useMemo(
    () => faqs[currentLanguage] || faqs.en,
    [currentLanguage]
  );
  const langTestimonials = useMemo(
    () => testimonials[currentLanguage] || testimonials.en,
    [currentLanguage]
  );

  useEffect(() => {
    if (selectedDate) {
      const calendarFormSection = document.getElementById(
        "calendar-form-section"
      );
      if (calendarFormSection) {
        calendarFormSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [selectedDate]);

  const handleDateChange = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isBooked = placeholderBookedDates.some(
      (bookedDate) => date.toDateString() === bookedDate.toDateString()
    );

    if (date < today || isBooked) {
      setSelectedDate(null);
    } else if (
      selectedDate &&
      selectedDate.toDateString() === date.toDateString()
    ) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
      setIsSubmitted(false);
      setSelectedPackage("");
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) return "react-calendar__tile--past";
      if (
        placeholderBookedDates.some(
          (d) => d.toDateString() === date.toDateString()
        )
      )
        return "react-calendar__tile--booked";
      if (selectedDate && date.toDateString() === selectedDate.toDateString())
        return "react-calendar__tile--current-selection";
      return "react-calendar__tile--available";
    }
    return null;
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <div className="page-content">
      <div className="page-title-container">
        <h1>{t("bookingTitle")}</h1>
      </div>
      <p className="section-subtitle">{t("bookingSubtitle")}</p>

      {/* How It Works Section (RESTORED) */}
      <section className="booking-page-section how-it-works-section">
        <h2>{t("bookingProcessTitle")}</h2>
        <Carousel>
          <div className="step-item">
            <div className="step-number">1</div>
            <h3>{t("bookingStep1Title")}</h3>
            <p>{t("bookingStep1Text")}</p>
          </div>
          <div className="step-item">
            <div className="step-number">2</div>
            <h3>{t("bookingStep2Title")}</h3>
            <p>{t("bookingStep2Text")}</p>
          </div>
          <div className="step-item">
            <div className="step-number">3</div>
            <h3>{t("bookingStep3Title")}</h3>
            <p>{t("bookingStep3Text")}</p>
          </div>
        </Carousel>
      </section>

      {/* Booking Packages Section (UPDATED) */}
      <section className="booking-page-section">
        <h2>{t("bookingPackagesTitle")}</h2>

        <h3 className="package-subsection-title">
          {t("packagesPersonalTitle")}
        </h3>
        <Carousel>
          {langPackages.personal.map((pkg) => (
            <div key={pkg.id} className="package-card">
              <h3>{pkg.title}</h3>
              <p>{pkg.description}</p>
              <ul>
                {pkg.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
              <Link
                to={`/gallery/${pkg.gallerySlug}`}
                className="secondary-button package-gallery-btn"
              >
                {t("viewGalleryButton")}
              </Link>
            </div>
          ))}
        </Carousel>

        <h3 className="package-subsection-title">
          {t("packagesCommercialTitle")}
        </h3>
        <Carousel>
          {langPackages.commercial.map((pkg) => (
            <div key={pkg.id} className="package-card">
              <h3>{pkg.title}</h3>
              <p>{pkg.description}</p>
              <ul>
                {pkg.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
              <Link
                to={`/gallery/${pkg.gallerySlug}`}
                className="secondary-button package-gallery-btn"
              >
                {t("viewGalleryButton")}
              </Link>
            </div>
          ))}
        </Carousel>
      </section>

      {/* Calendar & Form Section */}
      <section
        id="calendar-form-section"
        className={`booking-page-section calendar-form-section ${
          selectedDate ? "date-selected" : ""
        }`}
      >
        <h2>{t("bookingCalendarTitle")}</h2>
        <div className="calendar-and-form-wrapper">
          <div className="calendar-container">
            <Calendar
              onChange={handleDateChange}
              value={selectedDate}
              tileClassName={tileClassName}
              minDate={new Date()}
              locale={currentLanguage}
              selectRange={false}
              key={selectedDate ? selectedDate.toISOString() : "no-date"}
            />
          </div>
          {selectedDate && (
            <div className="booking-form-container">
              <h3>
                {t("bookingFormTitle")}{" "}
                {selectedDate.toLocaleDateString(
                  currentLanguage === "es" ? "es-ES" : "en-US",
                  { year: "numeric", month: "long", day: "numeric" }
                )}
              </h3>
              {!isSubmitted ? (
                <form onSubmit={handleFormSubmit} className="booking-form">
                  <input
                    type="text"
                    placeholder={t("bookingFormName")}
                    required
                  />
                  <input
                    type="email"
                    placeholder={t("bookingFormEmail")}
                    required
                  />
                  <select
                    name="eventType"
                    required
                    value={selectedPackage}
                    onChange={(e) => setSelectedPackage(e.target.value)}
                  >
                    <option value="" disabled>
                      {t("bookingFormSelectPackage")}
                    </option>
                    {allPackages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.title}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder={t("bookingFormVenue")}
                    required
                  />
                  <textarea
                    name="message"
                    placeholder={t("bookingFormMessage")}
                    rows="4"
                    required
                  ></textarea>
                  <button type="submit" className="btn-primary">
                    {t("bookingFormSubmit")}
                  </button>
                </form>
              ) : (
                <p className="form-success-message">
                  {t("bookingFormSuccess")}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section (RESTORED) */}
      <section className="booking-page-section">
        <h2>{t("bookingFaqTitle")}</h2>
        <div className="faq-container">
          {langFaqs.map((faq, index) => (
            <div key={index} className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(index)}>
                {faq.question}
                <span>{activeFaq === index ? "−" : "+"}</span>
              </button>
              <div
                className={`faq-answer ${activeFaq === index ? "open" : ""}`}
              >
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section (RESTORED) */}
      <section className="booking-page-section">
        <h2>{t("bookingTestimonialsTitle")}</h2>
        <div className="testimonials-grid">
          {langTestimonials.map((testimonial, index) => (
            <div key={index} className="testimonial-card">
              <p className="testimonial-quote">"{testimonial.quote}"</p>
              <p className="testimonial-author">- {testimonial.author}</p>
              <p className="testimonial-context">{testimonial.context}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Booking;
